'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocale } from 'next-intl';
import { Button } from '@/components/ui/button';
import { getAppUpdateCopy } from '@/i18n/app-update-copy';
import {
  useReloadBlocker,
  useReloadSafety,
} from '@/contexts/reload-safety-context';
import { getReloadSafety } from '@/lib/reload-blockers';
import { hasUnregisteredPageActivity } from '@/lib/web-build-page-activity';
import {
  createWebBuildPoller,
  fetchWebBuildIdentity,
  isIdleForBuildReload,
  LOADED_WEB_BUILD_ID,
  tryReloadForBuild,
  WEB_BUILD_POLL_INTERVAL_MS,
} from '@/lib/web-build-identity';
import { pendingSendStore } from '@/components/work-chats/pending-send-store';

const hasUnfinishedSend = () =>
  pendingSendStore.getSnapshot().some((item) => item.status !== 'sent');

/** Any of these means someone is using the tab; scroll covers nested readers. */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'input',
  'wheel',
  'touchstart',
  'scroll',
] as const;
const IDLE_CHECK_INTERVAL_MS = 60_000;
const DISMISSED_KEY = 'imai:web-build-dismissed';

function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

export function WebBuildUpdateController() {
  const copy = getAppUpdateCopy(useLocale());
  const [latest, setLatest] = useState<string | null>(null);
  const safety = useReloadSafety();
  const pendingSends = useSyncExternalStore(
    pendingSendStore.subscribe,
    hasUnfinishedSend,
    () => false
  );
  useReloadBlocker(
    'work-chat-pending-sends',
    pendingSends ? 'blocked' : 'safe'
  );
  useEffect(() => {
    const poller = createWebBuildPoller({
      fetchIdentity: fetchWebBuildIdentity,
      onIdentity: setLatest,
    });
    const pollVisible = () => {
      if (document.visibilityState === 'visible') void poller.poll();
    };
    void poller.poll();
    window.addEventListener('focus', pollVisible);
    document.addEventListener('visibilitychange', pollVisible);
    const interval = setInterval(pollVisible, WEB_BUILD_POLL_INTERVAL_MS);
    return () => {
      poller.dispose();
      clearInterval(interval);
      window.removeEventListener('focus', pollVisible);
      document.removeEventListener('visibilitychange', pollVisible);
    };
  }, []);
  const lastActivityAt = useRef(Date.now());
  // The card can be dismissed per build; the next deploy shows it again.
  const [dismissed, setDismissed] = useState<string | null>(null);
  useEffect(() => {
    setDismissed(readDismissed());
  }, []);
  // Where focus was before the card took it, so dismissing does not drop it.
  const returnFocus = useRef<HTMLElement | null>(null);
  const dismiss = () => {
    const target = returnFocus.current;
    if (target?.isConnected) target.focus({ preventScroll: true });
    setDismissed(latest);
    try {
      if (latest) window.sessionStorage.setItem(DISMISSED_KEY, latest);
    } catch {
      /* In-memory dismissal still holds for this page. */
    }
  };
  useEffect(() => {
    const markActive = () => {
      lastActivityAt.current = Date.now();
    };
    const options = { capture: true, passive: true } as const;
    for (const type of ACTIVITY_EVENTS)
      window.addEventListener(type, markActive, options);
    return () => {
      for (const type of ACTIVITY_EVENTS)
        window.removeEventListener(type, markActive, options);
    };
  }, []);
  useEffect(() => {
    // Automatic reload is only for a tab nobody is using (#7687): hidden and
    // without input for the idle window, on top of the registry saying safe.
    const attempt = () => {
      try {
        tryReloadForBuild({
          loaded: LOADED_WEB_BUILD_ID,
          latest,
          safety: getReloadSafety(),
          idle:
            isIdleForBuildReload({
              hidden: document.visibilityState === 'hidden',
              lastActivityAt: lastActivityAt.current,
              now: Date.now(),
            }) && !hasUnregisteredPageActivity(document),
          storage: window.sessionStorage,
          reload: () => window.location.reload(),
        });
      } catch {
        /* Storage access can itself throw. Keep the manual action. */
      }
    };
    attempt();
    document.addEventListener('visibilitychange', attempt);
    const interval = setInterval(attempt, IDLE_CHECK_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', attempt);
    };
  }, [latest, safety]);
  if (
    !latest ||
    !LOADED_WEB_BUILD_ID ||
    latest === LOADED_WEB_BUILD_ID ||
    dismissed === latest
  )
    return null;
  // Top of the screen on phones and narrow windows, clear of the composer and
  // the artifact panel footer; bottom corner from `sm` up.
  return (
    <div
      role="status"
      data-web-build-update="true"
      onFocusCapture={(event) => {
        const from = event.relatedTarget;
        if (from instanceof HTMLElement && !event.currentTarget.contains(from))
          returnFocus.current = from;
      }}
      className="bg-background border-border shadow-popover fixed inset-x-4 top-[calc(env(safe-area-inset-top)+0.75rem)] z-50 flex items-center gap-2 rounded-xl border p-3 sm:inset-x-auto sm:end-4 sm:top-auto sm:bottom-4 sm:max-w-sm"
    >
      <p className="text-muted-foreground min-w-0 flex-1 text-sm">
        {safety === 'blocked' ? copy.blocked : copy.description}
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => window.location.reload()}
      >
        {copy.reload}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        data-web-build-update-dismiss="true"
        onClick={dismiss}
      >
        {copy.dismiss}
      </Button>
    </div>
  );
}
